/**
 * Snippet-compat shim — everything the retiring per-site snippets still reach
 * for during rollout. The snippets live as WP code snippets ON THE CUSTOMER
 * SITES (the repo's snippets/ directory was only a reference copy, since
 * deleted) — this shim must stay until every site's cutover runbook is done,
 * then be deleted in one commit.
 *
 * - window.PaCartManager / window.PA.CartManager: the snippet cart_manager's
 *   global API names.
 * - paCartCount: sessionStorage mirror of the basket count some snippets read.
 * - pa_subscription_product_id_*: per-suite selection keys written by the old
 *   snippet layer to BOTH storages; cleared with the basket so no stale
 *   "selected" state survives "Tøm kurv".
 */

import PaCartManager, { readBasket } from './cart_manager.js';

const COUNT_KEY = 'paCartCount';

const LEGACY_SELECTION_KEYS = [
    'pa_subscription_product_id_tv',
    'pa_subscription_product_id_internet',
    'pa_subscription_product_id_fsecure',
    'pa_subscription_product_id_streaming',
    'pa_subscription_product_id_kn_streaming',
    'pa_subscription_product_id_cloud',
    'pa_subscription_product_id_udstyr',
    'pa_subscription_product_id_tilvalg',
];

/**
 * The snippets store the selected product id as a plain string in these keys
 * and re-select — or re-add — the product from them on their own pages. Drop
 * every key whose product is no longer in the basket, so removing a basket
 * line (trash icon on the cart page, single-select, clear) also deselects the
 * card on the snippet pages instead of letting it resurrect the line.
 */
function reconcileSelectionKeys() {
    const inBasket = new Set( readBasket().map( line => String( line.id ) ) );
    LEGACY_SELECTION_KEYS.forEach( key => {
        [ window.sessionStorage, window.localStorage ].forEach( storage => {
            const value = storage.getItem( key );
            if ( value != null && ! inBasket.has( String( value ).trim() ) ) {
                storage.removeItem( key );
            }
        } );
    } );
}

// Deferred so a snippet's select flow (write key → add to basket) has
// finished before the keys are checked against the basket.
let reconcileScheduled = false;
function scheduleReconcile() {
    if ( reconcileScheduled ) {
        return;
    }
    reconcileScheduled = true;
    setTimeout( function() {
        reconcileScheduled = false;
        reconcileSelectionKeys();
    }, 0 );
}

export function init() {
    window.PaCartManager = PaCartManager;
    window.PA = window.PA || {};
    window.PA.CartManager = PaCartManager;

    window.addEventListener( 'pa:cart:count', function( event ) {
        window.sessionStorage.setItem( COUNT_KEY, String( event.detail.count ) );
    } );

    window.addEventListener( 'pa_basket_update', scheduleReconcile );

    //NOT redundant with the reconcile above: scheduleReconcile is deferred
    //(setTimeout), and cart_manager.clear() may reload the page immediately
    //after firing this event — the synchronous scrub is what survives the
    //reload.
    window.addEventListener( 'pa:cart:cleared', function() {
        LEGACY_SELECTION_KEYS.forEach( key => {
            window.sessionStorage.removeItem( key );
            window.localStorage.removeItem( key );
        } );
    } );

    // Scrub keys left behind by a previous page (e.g. a line removed right
    // before navigating here).
    reconcileSelectionKeys();
}

export default { init };
